import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { NumericInputWithSlider } from '../NumericInputWithSlider';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { visualConfig } from '../../config/visualConfig';

interface ContainerInputsTabProps {
  containerLength: number;
  containerWidth: number;
  containerHeight: number;
  containerWeightCapacity: number;
  unitSystem: 'metric' | 'imperial';
  setContainerLengthAndReset: (value: number) => void;
  setContainerWidthAndReset: (value: number) => void;
  setContainerHeightAndReset: (value: number) => void;
  setContainerWeightCapacityAndReset: (value: number) => void;
  goToPreviousTab: () => void;
  goToNextTab: () => void;
}

export function ContainerInputsTab({
  containerLength,
  containerWidth,
  containerHeight,
  containerWeightCapacity,
  unitSystem,
  setContainerLengthAndReset,
  setContainerWidthAndReset,
  setContainerHeightAndReset,
  setContainerWeightCapacityAndReset,
  goToPreviousTab,
  goToNextTab
}: ContainerInputsTabProps) {
  const colors = visualConfig.colors.container;
  
  const commonContainerSizes = [
    { label: "20ft Standard Container (609.6 x 243.8 x 259.1 cm, 28000 kg)", length: 609.6, width: 243.8, height: 259.1, maxWeight: 28000 },
    { label: "40ft Standard Container (1219.2 x 243.8 x 259.1 cm, 26000 kg)", length: 1219.2, width: 243.8, height: 259.1, maxWeight: 26000 },
    { label: "40ft High Cube Container (1219.2 x 243.8 x 289.6 cm, 26000 kg)", length: 1219.2, width: 243.8, height: 289.6, maxWeight: 26000 },
  ];

  return (
    <Card className={`${visualConfig.layout.card.minHeight} flex flex-col ${visualConfig.layout.card.background} ${visualConfig.layout.card.shape} overflow-hidden`}>
      <CardHeader className={`bg-gradient-to-r ${colors.gradient} text-white ${visualConfig.layout.header.padding}`}>
        <CardTitle className={visualConfig.layout.header.title}>Container Dimensions & Capacity</CardTitle>
        <CardDescription className={`text-orange-100 ${visualConfig.layout.header.description}`}>
          Enter the dimensions and weight capacity of your container.
        </CardDescription>
      </CardHeader>
      <CardContent className={`${visualConfig.layout.content.spacing} ${visualConfig.layout.content.padding}`}>
        <div className={visualConfig.interactive.form.spacing}>
          <Label htmlFor="container-size-select" className={`${visualConfig.typography.subsectionTitle} ${visualConfig.colors.neutral.text.primary}`}>Common Container Sizes</Label>
          <Select onValueChange={(value) => {
            const selectedSize = commonContainerSizes.find(size => `${size.length}x${size.width}x${size.height}` === value);
            if (selectedSize) {
              setContainerLengthAndReset(selectedSize.length);
              setContainerWidthAndReset(selectedSize.width);
              setContainerHeightAndReset(selectedSize.height);
              setContainerWeightCapacityAndReset(selectedSize.maxWeight);
            }
          }} value={`${containerLength}x${containerWidth}x${containerHeight}`}>
            <SelectTrigger id="container-size-select" className={visualConfig.interactive.select.shape}>
              <SelectValue placeholder="Select a common container size" />
            </SelectTrigger>
            <SelectContent>
              {commonContainerSizes.map((size) => (
                <SelectItem key={`${size.length}x${size.width}x${size.height}`} value={`${size.length}x${size.width}x${size.height}`}>
                  {size.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <NumericInputWithSlider
          id="container-length"
          baseLabel="Custom Container Length"
          value={containerLength}
          onChange={setContainerLengthAndReset}
          min={100}
          max={2000}
          step={unitSystem === 'metric' ? 1 : 0.1}
          unitSystem={unitSystem}
          unitType="dimension"
        />
        <NumericInputWithSlider
          id="container-width"
          baseLabel="Custom Container Width"
          value={containerWidth}
          onChange={setContainerWidthAndReset}
          min={100}
          max={500}
          step={unitSystem === 'metric' ? 1 : 0.1}
          unitSystem={unitSystem}
          unitType="dimension"
        />
        <NumericInputWithSlider
          id="container-height"
          baseLabel="Custom Container Height"
          value={containerHeight}
          onChange={setContainerHeightAndReset}
          min={100}
          max={500}
          step={unitSystem === 'metric' ? 1 : 0.1}
          unitSystem={unitSystem}
          unitType="dimension"
        />
        <NumericInputWithSlider
          id="container-weight-capacity"
          baseLabel="Weight Capacity"
          value={containerWeightCapacity}
          onChange={setContainerWeightCapacityAndReset}
          min={1000}
          max={50000}
          step={unitSystem === 'metric' ? 100 : 1}
          unitSystem={unitSystem}
          unitType="weight"
        />
      </CardContent>
      <div className={`${visualConfig.layout.footer.layout} ${visualConfig.layout.footer.padding}`}>
        <button
          onClick={goToPreviousTab}
          className={`${visualConfig.interactive.button.base} ${visualConfig.colors.neutral.previous} ${visualConfig.interactive.button.focus} focus:ring-gray-200`}
        >
          Previous
        </button>
        <button
          onClick={goToNextTab}
          className={`${visualConfig.interactive.button.base} ${colors.button} ${visualConfig.interactive.button.focus} ${colors.ring}`}
        >
          Next
        </button>
      </div>
    </Card>
  );
} 